import React, { useMemo, useState } from 'react';
import { Stack, Typography, Box, useTheme, useMediaQuery, Skeleton } from '@mui/material';
import { MetricCard } from './MetricCard';
import { MetricsChart } from './MetricsChart';
import { ChartControls } from './ChartControls';
import { YearSelector } from './YearSelector';

import { YearData, MetricType, PeriodType } from '../../../store/modules/insights/types';
import { getQuarterMonthLabel } from '../../../util/insightsUtils';

interface InsightsDashboardProps {
  data?: YearData;
  allYears?: YearData[];
  selectedYear: number;
  onYearChange: (year: number) => void;
  isLoading: boolean;
  isParentMerchant?: boolean;
}

export const InsightsDashboard: React.FC<InsightsDashboardProps> = ({
  data,
  allYears = [],
  selectedYear,
  onYearChange,
  isLoading,
  isParentMerchant = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedMetric, setSelectedMetric] = useState<MetricType>('gmv');
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('monthly');

  const availableYears = useMemo(() => {
    const dataYears = allYears.map((y) => y.number);
    const currentYear = new Date().getFullYear();

    // If we have data years, include them plus current year
    if (dataYears.length > 0) {
      const minDataYear = dataYears.reduce((min, year) => Math.min(min, year), dataYears[0]);
      const maxDataYear = dataYears.reduce((max, year) => Math.max(max, year), dataYears[0]);
      const minYear = minDataYear;
      const maxYear = Math.max(currentYear, maxDataYear);

      // Create range from min data year to current year (or max data year if later)
      const yearRange: number[] = [];
      for (let year = minYear; year <= maxYear; year++) {
        yearRange.push(year);
      }
      return yearRange.sort((a, b) => b - a); // Descending order
    }

    // If no data, just show current year
    return [currentYear];
  }, [allYears]);

  const yearLabel = useMemo(() => (data?.isCurrentPeriod ? 'YTD' : ''), [data?.isCurrentPeriod]);

  // Sort quarters in reverse order (Q4, Q3, Q2, Q1) for 2x2 layout
  const sortedQuarters = useMemo(() => {
    if (!data?.quarters) return [];
    return [...data.quarters].sort((a, b) => {
      const quarterOrder = { Q4: 4, Q3: 3, Q2: 2, Q1: 1 };
      const aOrder = quarterOrder[a.label as keyof typeof quarterOrder] || 0;
      const bOrder = quarterOrder[b.label as keyof typeof quarterOrder] || 0;
      return bOrder - aOrder; // Descending order
    });
  }, [data?.quarters]);

  return (
    <Stack spacing={0} sx={{ mt: -1, maxWidth: '944px' }}>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: isParentMerchant ? 1 : 0,
          }}
        >
          {availableYears.length > 0 ? (
            <YearSelector
              years={availableYears}
              selectedYear={selectedYear}
              onYearChange={onYearChange}
              disabled={isLoading}
            />
          ) : isLoading ? (
            <Skeleton
              variant="rectangular"
              height="44px"
              width="95px"
              sx={{ borderRadius: '8px' }}
            />
          ) : (
            <YearSelector
              years={[selectedYear]}
              selectedYear={selectedYear}
              onYearChange={onYearChange}
              disabled={false}
            />
          )}
        </Box>

        {isParentMerchant && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="small" color="text.secondary">
              <Typography component="span" fontWeight="semibold" variant="small">
                Parent Merchant View -{' '}
              </Typography>
              Metrics shown below represent totals across all child merchants.
            </Typography>
          </Box>
        )}
      </Box>

      <Stack spacing={1}>
        <Typography variant="xl" fontWeight="semibold">
          Performance Trends
        </Typography>

        {isLoading ? (
          <Skeleton variant="rectangular" height={isMobile ? '300px' : '472px'} />
        ) : (
          <>
            <ChartControls
              metricType={selectedMetric}
              onMetricChange={setSelectedMetric}
              periodType={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              disabled={isLoading}
              isParentMerchant={isParentMerchant}
            />

            <MetricsChart
              data={data}
              metricType={selectedMetric}
              periodType={selectedPeriod}
              isLoading={isLoading}
            />
          </>
        )}
      </Stack>

      <Stack spacing={1} sx={{ mt: 6 }}>
        <Typography variant="xl" fontWeight="semibold">
          Yearly Overview
        </Typography>
        <MetricCard
          title={selectedYear.toString()}
          subtitle={yearLabel}
          metrics={data?.metrics}
          hasData={
            !!(
              data?.metrics?.gmv ||
              data?.metrics?.transactionCount ||
              data?.metrics?.refundGmv ||
              data?.metrics?.refundTransactionCount
            )
          }
          isLoading={isLoading}
        />
      </Stack>

      <Stack spacing={1} sx={{ mt: 6 }}>
        <Typography variant="xl" fontWeight="semibold">
          Quarterly Overview
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
          }}
        >
          {sortedQuarters.length > 0
            ? sortedQuarters.map((quarter) => (
                <Box
                  key={quarter.label}
                  sx={{
                    flex: isMobile ? '1 1 100%' : '0 1 calc(50% - 8px)',
                    maxWidth: isMobile ? '100%' : 'calc(50% - 8px)',
                    minWidth: 0,
                  }}
                >
                  <MetricCard
                    title={quarter.label}
                    subtitle={
                      quarter.isCurrentPeriod
                        ? `QTD ${getQuarterMonthLabel(quarter.label)}`
                        : getQuarterMonthLabel(quarter.label)
                    }
                    metrics={quarter.metrics}
                    hasData={
                      !!(
                        quarter.metrics?.gmv ||
                        quarter.metrics?.transactionCount ||
                        quarter.metrics?.refundGmv ||
                        quarter.metrics?.refundTransactionCount
                      )
                    }
                    isLoading={isLoading}
                    isParentMerchant={isParentMerchant}
                  />
                </Box>
              ))
            : ['Q4', 'Q3', 'Q2', 'Q1'].map((quarter) => (
                <Box
                  key={quarter}
                  sx={{
                    flex: isMobile ? '1 1 100%' : '0 1 calc(50% - 8px)',
                    maxWidth: isMobile ? '100%' : 'calc(50% - 8px)',
                    minWidth: 0,
                  }}
                >
                  <MetricCard
                    title={quarter}
                    subtitle={getQuarterMonthLabel(quarter)}
                    metrics={undefined}
                    hasData={false}
                    isLoading={isLoading}
                    isParentMerchant={isParentMerchant}
                  />
                </Box>
              ))}
        </Box>
      </Stack>
    </Stack>
  );
};
